// ── VerifyIdentity — Email + SMS verification component ──────────────────
// Used in: Profile settings, post-onboarding prompt
// Props:
//   mode       — 'email' | 'phone' | 'both'
//   userEmail  — pre-fill
//   onComplete — (status: {email, phone}) => void
//   onSkip     — () => void
//   compact    — bool: inline card vs full-screen

import { useState, useEffect, useCallback } from 'react';
import { useTr } from '../utils/useTr';
// Verification via Supabase Auth (email) + Edge Function (SMS)
import {
  formatPhone, maskEmail, maskPhone, saveVerificationStatus, getVerificationStatus,
  sendEmailOTP, sendSmsOTP,
} from '../utils/verification';

const G = {
  gold:'var(--gold)', goldPale:'var(--gold-pale)', goldBdr:'var(--gold-border)',
  border:'var(--border)', card:'var(--dark)', muted:'var(--muted)',
  white:'var(--white)', green:'#4EC994', red:'#E07070',
  sans:"'Outfit',sans-serif",
};

// ── OTP input — 6 boxes ───────────────────────────────────────────────────
function OTPInput({ value, onChange, hasError }) {
  const digits = value.padEnd(6, '').split('').slice(0, 6);
  const handleKey = (e, i) => {
    if (e.key === 'Backspace') {
      const next = value.slice(0, i > 0 ? i - (value.length <= i ? 1 : 0) : 0);
      onChange(next);
      if (i > 0 && value.length <= i) setTimeout(() => document.getElementById(`otp-${i-1}`)?.focus(), 0);
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, 6);
    onChange(next);
    if (i < 5) setTimeout(() => document.getElementById(`otp-${i+1}`)?.focus(), 0);
  };
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted); e.preventDefault(); }
  };

  return (
    <div style={{ display:'flex', gap:'0.4rem', justifyContent:'center', margin:'1.2rem 0' }}>
      {digits.map((d, i) => (
        <input
          key={i} id={`otp-${i}`}
          value={d} onChange={() => {}}
          onKeyDown={e => handleKey(e, i)}
          onPaste={handlePaste}
          onFocus={() => document.getElementById(`otp-${i}`)?.select()}
          maxLength={1} inputMode="numeric"
          style={{ width:42, height:50, textAlign:'center', fontFamily:'monospace', fontSize:'1.4rem', fontWeight:700, color: hasError ? G.red : G.white, background: d ? 'rgba(201,168,76,0.08)' : G.card, border:`1.5px solid ${hasError ? 'rgba(224,112,112,0.5)' : d ? G.goldBdr : G.border}`, borderRadius:8, outline:'none', transition:'border-color 0.15s', caretColor:'transparent' }}
        />
      ))}
    </div>
  );
}

// ── Resend timer ──────────────────────────────────────────────────────────
function ResendTimer({ onResend, tr }) {
  const [secs, setSecs] = useState(60);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setTimeout(() => setSecs(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secs]);
  return (
    <button onClick={() => { if (secs <= 0) { setSecs(60); onResend(); } }} disabled={secs > 0}
      style={{ background:'none', border:'none', color: secs > 0 ? G.muted : G.gold, fontFamily:G.sans, fontSize:'0.72rem', cursor: secs > 0 ? 'default' : 'pointer', padding:0 }}>
      {secs > 0 ? tr('verify.resend_in', { s: secs }) : tr('verify.resend')}
    </button>
  );
}

// ── Single step: email or phone ───────────────────────────────────────────
function VerifyStep({ type, initialValue, onVerified, tr }) {
  const [phase, setPhase]     = useState('input');   // input | sent | success | error
  const [value, setValue]     = useState(initialValue || '');
  const [code, setCode]       = useState('');
  const [masked, setMasked]   = useState('');
  const [errMsg, setErrMsg]   = useState('');
  const [loading, setLoading] = useState(false);

  const isEmail = type === 'email';

  const handleSend = useCallback(async () => {
    if (!value.trim()) return;
    setLoading(true); setErrMsg('');
    try {
      const result = isEmail ? await sendEmailOTP(value) : await sendSmsOTP(value);
      setMasked(isEmail ? result.maskedEmail : result.maskedPhone);
      setPhase('sent');
    } catch { setErrMsg('Failed to send — try again.'); }
    finally { setLoading(false); }
  }, [value, isEmail]);

  const handleVerify = useCallback(async () => {
    if (code.length < 6) return;
    setLoading(true); setErrMsg('');
    try {
      let verified = false;
      if (isEmail) {
        const { supabase } = await import('../utils/supabase');
        const { error } = await supabase.auth.verifyOtp({ email: value, token: code, type: 'email' });
        verified = !error;
        if (error) throw new Error(error.message);
      } else {
        const { callAPI } = await import('../utils/api');
        const data = await callAPI('/sms/verify', 'POST', { phone: value, code });
        verified = data.verified;
        if (!verified) throw new Error('Invalid code');
      }
      saveVerificationStatus(type, value);
      setPhase('success');
      setTimeout(() => onVerified(value), 800);
    } catch (e) {
      setErrMsg(e.code === 'expired' ? tr('verify.expired') : tr('verify.error'));
      setCode('');
      setLoading(false);
    }
  }, [code, value, isEmail, type, onVerified, tr]);

  useEffect(() => { if (code.length === 6 && phase === 'sent') handleVerify(); }, [code, phase, handleVerify]);

  if (phase === 'success') {
    return (
      <div style={{ textAlign:'center', padding:'1.5rem 0' }}>
        <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(78,201,148,0.15)', border:'1px solid rgba(78,201,148,0.35)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem', margin:'0 auto 0.8rem' }}>✓</div>
        <div style={{ fontSize:'0.95rem', color:G.green, fontWeight:600 }}>{tr(isEmail ? 'verify.success_email' : 'verify.success_phone')}</div>
      </div>
    );
  }

  if (phase === 'sent') {
    return (
      <div>
        <p style={{ fontSize:'0.78rem', color:G.muted, textAlign:'center', lineHeight:1.6, marginBottom:0 }}>
          {tr(isEmail ? 'verify.email_sub' : 'verify.sms_sub', { [isEmail ? 'email' : 'phone']: masked })}
        </p>
        <OTPInput value={code} onChange={setCode} hasError={!!errMsg}/>
        {errMsg && <p style={{ fontSize:'0.7rem', color:G.red, textAlign:'center', margin:'0 0 0.5rem' }}>{errMsg}</p>}
        <div style={{ textAlign:'center', marginBottom:'0.5rem' }}>
          <ResendTimer onResend={handleSend} tr={tr}/>
        </div>
        <button onClick={handleVerify} disabled={code.length < 6 || loading}
          style={{ width:'100%', padding:'0.75rem', background: code.length >= 6 ? G.gold : 'rgba(255,255,255,0.08)', border:'none', borderRadius:8, color: code.length >= 6 ? '#0A0A0A' : G.muted, fontFamily:G.sans, fontSize:'0.82rem', fontWeight:700, cursor: code.length >= 6 ? 'pointer' : 'default' }}>
          {loading ? '...' : tr('verify.confirm')}
        </button>
        <p style={{ fontSize:'0.62rem', color:G.muted, textAlign:'center', marginTop:'0.6rem' }}>
          Test code: <strong style={{color:G.gold}}>000000</strong>
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom:'0.7rem' }}>
        <label style={{ fontSize:'0.62rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:G.muted, display:'block', marginBottom:'0.3rem' }}>
          {tr(isEmail ? 'verify.email_title' : 'verify.phone_label')}
        </label>
        <input
          value={value} onChange={e => setValue(isEmail ? e.target.value : formatPhone(e.target.value))}
          type={isEmail ? 'email' : 'tel'}
          placeholder={tr(isEmail ? 'verify.email_title' : 'verify.phone_placeholder')}
          style={{ width:'100%', padding:'0.6rem 0.8rem', background:G.card, border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, fontSize:'0.85rem', outline:'none', boxSizing:'border-box' }}
        />
      </div>
      <div style={{ padding:'0.55rem 0.75rem', background:'rgba(78,201,148,0.06)', border:'1px solid rgba(78,201,148,0.18)', borderRadius:6, marginBottom:'0.9rem' }}>
        <p style={{ fontSize:'0.68rem', color:'#4EC994', margin:0 }}>✦ {tr('verify.why')}</p>
      </div>
      <button onClick={handleSend} disabled={!value.trim() || loading}
        style={{ width:'100%', padding:'0.75rem', background: value.trim() ? G.gold : 'rgba(255,255,255,0.08)', border:'none', borderRadius:8, color: value.trim() ? '#0A0A0A' : G.muted, fontFamily:G.sans, fontSize:'0.82rem', fontWeight:700, cursor: value.trim() ? 'pointer' : 'default' }}>
        {loading ? 'Sending...' : tr('verify.send_code')}
      </button>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────
export default function VerifyIdentity({ mode = 'both', userEmail, onComplete, onSkip, compact = false }) {
  const tr = useTr();
  const existing = getVerificationStatus();
  const [activeStep, setActiveStep] = useState(
    mode === 'phone' ? 'phone' :
    existing.email ? 'phone' : 'email'
  );
  const [doneEmail, setDoneEmail] = useState(!!existing.email);
  const [donePhone, setDonePhone] = useState(!!existing.phone);

  const handleEmailVerified = (email) => {
    setDoneEmail(true);
    if (mode === 'both') setActiveStep('phone');
    else onComplete?.({ email, phone: existing.phone?.value });
  };

  const handlePhoneVerified = (phone) => {
    setDonePhone(true);
    onComplete?.({ email: existing.email?.value, phone });
  };

  const allDone = (mode === 'email' && doneEmail) || (mode === 'phone' && donePhone) || (mode === 'both' && doneEmail && donePhone);

  if (allDone) {
    return (
      <div style={{ textAlign:'center', padding: compact ? '1rem' : '2rem' }}>
        <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(78,201,148,0.15)', border:'1px solid rgba(78,201,148,0.35)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.5rem', margin:'0 auto 0.8rem' }}>✓</div>
        <div style={{ fontSize:'1rem', color:G.green, fontWeight:600 }}>{tr('verify.both_done')}</div>
      </div>
    );
  }

  const wrap = compact
    ? { background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:'1.2rem' }
    : { padding:'1.5rem' };

  return (
    <div style={wrap}>
      {!compact && (
        <div style={{ marginBottom:'1.2rem' }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', color:G.white, marginBottom:'0.2rem' }}>
            {tr(activeStep === 'email' ? 'verify.email_title' : 'verify.sms_title')}
          </div>
          {mode === 'both' && (
            <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.6rem' }}>
              {['email','phone'].map(s => (
                <div key={s} style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontSize:'0.65rem', color: s === activeStep ? G.gold : doneEmail && s==='email' || donePhone && s==='phone' ? G.green : G.muted }}>
                  <div style={{ width:16, height:16, borderRadius:'50%', background: (s==='email'&&doneEmail)||(s==='phone'&&donePhone) ? 'rgba(78,201,148,0.2)' : s===activeStep ? G.goldPale : 'transparent', border:`1px solid ${(s==='email'&&doneEmail)||(s==='phone'&&donePhone) ? 'rgba(78,201,148,0.4)' : s===activeStep ? G.goldBdr : G.border}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.55rem' }}>
                    {(s==='email'&&doneEmail)||(s==='phone'&&donePhone) ? '✓' : s==='email' ? '1' : '2'}
                  </div>
                  {s === 'email' ? 'Email' : 'Phone'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeStep === 'email' && !doneEmail && (
        <VerifyStep type="email" initialValue={userEmail} onVerified={handleEmailVerified} tr={tr}/>
      )}
      {activeStep === 'phone' && !donePhone && (
        <VerifyStep type="phone" onVerified={handlePhoneVerified} tr={tr}/>
      )}

      {onSkip && !allDone && (
        <button onClick={onSkip}
          style={{ display:'block', width:'100%', marginTop:'0.7rem', padding:'0.45rem', background:'none', border:'none', color:G.muted, fontFamily:G.sans, fontSize:'0.72rem', cursor:'pointer', textAlign:'center' }}>
          {tr('verify.skip')} →
        </button>
      )}
    </div>
  );
}

// ── Verification badges (for profile display) ────────────────────────────
export function VerificationBadges({ compact = false }) {
  const tr = useTr();
  const status = getVerificationStatus();
  if (!status.email && !status.phone) return null;
  return (
    <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
      {status.email && (
        <span style={{ fontSize:'0.6rem', padding:'0.15rem 0.5rem', background:'rgba(78,201,148,0.1)', border:'1px solid rgba(78,201,148,0.3)', borderRadius:99, color:G.green, display:'flex', alignItems:'center', gap:'0.25rem' }}>
          ✓ {compact ? 'Email' : tr('verify.badge_email')}
        </span>
      )}
      {status.phone && (
        <span style={{ fontSize:'0.6rem', padding:'0.15rem 0.5rem', background:'rgba(91,155,213,0.1)', border:'1px solid rgba(91,155,213,0.3)', borderRadius:99, color:'#5B9BD5', display:'flex', alignItems:'center', gap:'0.25rem' }}>
          ✓ {compact ? 'SMS' : tr('verify.badge_phone')}
        </span>
      )}
    </div>
  );
}
